import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Outlet } from 'react-router'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { OrgStatus, useCurrentUser } from '@/models/user'
import { useComplianceScope } from '@/models/vault'
import { VaultImportStatus } from './Vault/onboarding/VaultImportStatus'
import { VaultOnboarding } from './Vault/onboarding/VaultOnboarding'
import { VaultProvider } from './Vault/VaultContext'
import { VaultHeroBlock } from './Vault/VaultHeroBlock'
import { VaultTitle } from './Vault/VaultTitle'

const vaultHref = (path: string) => (path ? `/insure/vault/${path}` : '/insure/vault')

const CLOSED_UNTIL_ACTIVE = 'The vault opens once your fleet is active with Cai.'
const SWITCHED_OFF = 'The compliance vault is not switched on for your fleet yet.'

export function VaultPage() {
  const { user } = useCurrentUser({ enabled: true })
  // Finishing the onboarding drops straight into the live vault, without a reload.
  const [onboarded, setOnboarded] = useState(false)
  const [importing, setImporting] = useState(false)
  // Staff can carry no current org; the type says otherwise, the API does not.
  const orgId = user?.currentOrg?.id
  const { scope, isLoading: isScopeLoading, isError: scopeFailed, refetch } = useComplianceScope(orgId)
  // Why the vault cannot open at all — the same two reasons the API gives.
  const closed =
    user && !user.isCaiAdmin
      ? user.currentOrg?.status !== OrgStatus.ACTIVE
        ? CLOSED_UNTIL_ACTIVE
        : !user.flags['ENABLE_VAULT']
          ? SWITCHED_OFF
          : null
      : null
  // Onboarding only reaches fleets that can finish it; the rest cannot confirm a scope.
  // A failed read is not an unconfirmed scope: onboarding from it would overwrite a real one.
  const settingUp = !closed && !scopeFailed && !onboarded && !scope?.dateConfirmed

  return (
    <>
      <Helmet>
        <title>Compliance vault</title>
      </Helmet>
      <div className="flex flex-col gap-6">
        {closed ? (
          // The sidebar hides the entry; the URL must not outrun the switch, nor an inactive fleet.
          <>
            <VaultTitle />
            <Banner variant="info">{closed}</Banner>
          </>
        ) : !orgId || isScopeLoading ? (
          <>
            <VaultTitle />
            <SkeletonRows />
          </>
        ) : scopeFailed ? (
          <>
            <VaultTitle />
            <Banner variant="error">
              We couldn&rsquo;t load your vault.{' '}
              <Button variant="link" size="xs" className="h-auto p-0" onClick={() => void refetch()}>
                Try again
              </Button>
            </Banner>
          </>
        ) : settingUp ? (
          <>
            <VaultTitle aside={<VaultImportStatus orgId={orgId} running={importing} />} />
            <VaultOnboarding
              orgId={orgId}
              onImportStart={() => setImporting(true)}
              onFinish={() => setOnboarded(true)}
            />
          </>
        ) : (
          <VaultProvider orgId={orgId} href={vaultHref}>
            <VaultHeroBlock />
            <Outlet />
          </VaultProvider>
        )}
      </div>
    </>
  )
}
